import { ReactNode } from 'react';

import { Button, ButtonProps, StackProps } from '@mui/material';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';

interface ButtonConfig {
    label?: string;
    buttonProps?: ButtonProps;
    eventTag?: string;
    eventProps?: Record<string, any>;
}

export interface ConfirmationDrawerProps extends DrawerComponentProps {
    cancelButton?: ButtonConfig;
    closeOnCancel?: boolean;
    confirmButton?: ButtonConfig;
    content?: ReactNode;
    propName?: string;
    /** Override styles on the button container — e.g. `{ flexDirection: 'column-reverse' }` to flip button order */
    actionsContainerProps?: StackProps['sx'];
}

function ConfirmationDrawer(props: ConfirmationDrawerProps) {
    const {
        cancelButton,
        confirmButton,
        closeOnCancel = false,
        actionsContainerProps = {},
        content,
        onClose,
        onNext,
        onPrevious,
        propName = 'confirmed',
        title,
        ...payload
    } = props;

    const { trackEvent } = useAnalytics();

    const {
        label: confirmButtonLabel = 'Yes',
        buttonProps: confirmButtonProps,
        eventTag: confirmEventTag,
        eventProps: confirmEventProps = {},
    } = confirmButton || {};

    const {
        label: cancelButtonLabel = 'Cancel',
        buttonProps: cancelButtonProps,
        eventTag: cancelEventTag,
        eventProps: cancelEventProps = {},
    } = cancelButton || {};

    const handleClickConfirmAction = () => {
        if (confirmEventTag) {
            trackEvent(confirmEventTag, { ...confirmEventProps, objectType: ObjectTypes.Button });
        }
        onNext({ ...payload, [propName]: true });
    };

    const handleClickCancelAction = () => {
        if (cancelEventTag) {
            trackEvent(cancelEventTag, { ...cancelEventProps, objectType: ObjectTypes.Button });
        }

        if (closeOnCancel) {
            onClose();
        } else {
            onPrevious(false);
        }
    };

    return (
        <>
            {title && <DrawerTitle title={title} />}

            <DrawerBody allowShrink>{content}</DrawerBody>

            <DrawerFooter sx={{ gap: 2.5, pb: 4, ...actionsContainerProps }}>
                <Button
                    {...confirmButtonProps}
                    color={confirmButtonProps?.color || 'primary'}
                    disableElevation={confirmButtonProps?.disableElevation || true}
                    fullWidth={confirmButtonProps?.fullWidth || true}
                    size={confirmButtonProps?.size || undefined}
                    variant={confirmButtonProps?.variant || 'contained'}
                    onClick={handleClickConfirmAction}
                >
                    {confirmButtonLabel}
                </Button>

                {cancelButton && (
                    <Button
                        {...cancelButtonProps}
                        color={cancelButtonProps?.color || 'primary'}
                        disableElevation={cancelButtonProps?.disableElevation || true}
                        fullWidth={cancelButtonProps?.fullWidth || true}
                        size={cancelButtonProps?.size || undefined}
                        variant={cancelButtonProps?.variant || 'outlined'}
                        onClick={handleClickCancelAction}
                    >
                        {cancelButtonLabel}
                    </Button>
                )}
            </DrawerFooter>
        </>
    );
}

export default ConfirmationDrawer;
